import React, { useEffect, useRef, useState } from 'react';

/* ============================================================
   CursorFX — the "surprise as you move the mouse".
   A yellow glow + a lerped ring + a precise dot replace the
   native cursor on fine pointers. The ring swells over targets.
   ============================================================ */
export function CursorFX() {
  const glow = useRef(null);
  const dot = useRef(null);
  const ring = useRef(null);
  const pos = useRef({ x: 0, y: 0 });
  const ringPos = useRef({ x: 0, y: 0 });
  const [isFine, setIsFine] = useState(false);

  useEffect(() => {
    if (!window.matchMedia('(pointer: fine)').matches) return;
    setIsFine(true);

    document.body.classList.add('ba-custom-cursor');

    const onMove = (e) => {
      pos.current = { x: e.clientX, y: e.clientY };
      if (glow.current) glow.current.style.transform = `translate(${e.clientX}px, ${e.clientY}px)`;
      if (dot.current) dot.current.style.transform = `translate(${e.clientX}px, ${e.clientY}px)`;
      const t = e.target.closest('a, button, [data-cursor]');
      if (ring.current) ring.current.dataset.hot = t ? '1' : '0';
    };

    let raf;
    const loop = () => {
      ringPos.current.x += (pos.current.x - ringPos.current.x) * 0.18;
      ringPos.current.y += (pos.current.y - ringPos.current.y) * 0.18;
      if (ring.current) ring.current.style.transform = `translate(${ringPos.current.x}px, ${ringPos.current.y}px)`;
      raf = requestAnimationFrame(loop);
    };
    loop();
    window.addEventListener('mousemove', onMove);
    return () => {
      window.removeEventListener('mousemove', onMove);
      cancelAnimationFrame(raf);
      document.body.classList.remove('ba-custom-cursor');
    };
  }, []);

  // Coarse pointers (touch) never get the custom cursor layer.
  if (!isFine) return null;

  return (
    <>
      <div ref={glow} aria-hidden="true" style={{
        position: 'fixed', top: 0, left: 0, width: 600, height: 600, marginLeft: -300, marginTop: -300,
        borderRadius: '50%', pointerEvents: 'none', zIndex: 1, mixBlendMode: 'screen',
        background: 'radial-gradient(circle, var(--yellow-a16), transparent 60%)', willChange: 'transform' }} />
      <div ref={ring} data-hot="0" aria-hidden="true" className="cursor-ring" style={{
        position: 'fixed', top: 0, left: 0, width: 36, height: 36, marginLeft: -18, marginTop: -18,
        border: '1.5px solid var(--yellow-a64)', borderRadius: '50%', pointerEvents: 'none', zIndex: 9999,
        willChange: 'transform', transition: 'width .25s var(--ease-out), height .25s var(--ease-out), margin .25s var(--ease-out)' }} />
      <div ref={dot} aria-hidden="true" style={{
        position: 'fixed', top: 0, left: 0, width: 6, height: 6, marginLeft: -3, marginTop: -3,
        background: 'var(--accent)', borderRadius: '50%', pointerEvents: 'none', zIndex: 9999, willChange: 'transform' }} />
    </>
  );
}

/* ============================================================
   ScrollProgress — thin yellow bar at the very top.
   ============================================================ */
export function ScrollProgress() {
  const bar = useRef(null);
  useEffect(() => {
    const onScroll = () => {
      const h = document.documentElement;
      const p = h.scrollTop / (h.scrollHeight - h.clientHeight || 1);
      if (bar.current) bar.current.style.transform = `scaleX(${p})`;
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  return <div ref={bar} aria-hidden="true" style={{
    position: 'fixed', top: 0, left: 0, right: 0, height: 2, background: 'var(--accent)', transformOrigin: '0 50%',
    transform: 'scaleX(0)', zIndex: 10000, boxShadow: '0 0 12px var(--yellow-a64)' }} />;
}

/* ============================================================
   Reveal — fade-up on scroll entry (IntersectionObserver).
   ============================================================ */
export function Reveal({ children, delay = 0, y = 28, as = 'div', style = {}, ...rest }) {
  const ref = useRef(null);
  const [shown, setShown] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) { setShown(true); io.unobserve(el); } });
    }, { threshold: 0.15, rootMargin: '0px 0px -8% 0px' });
    io.observe(el);
    return () => io.disconnect();
  }, []);
  const Tag = as;
  return (
    <Tag ref={ref} style={{
      opacity: shown ? 1 : 0,
      transform: shown ? 'none' : `translateY(${y}px)`,
      transition: `opacity .9s var(--ease-out) ${delay}ms, transform .9s var(--ease-out) ${delay}ms`,
      ...style }} {...rest}>{children}</Tag>
  );
}

/* ============================================================
   CountUp — animated counter that runs when scrolled into view.
   Handles suffixed values like "6M+", "200k+", "20+".
   ============================================================ */
export function CountUp({ value }) {
  const ref = useRef(null);
  const [disp, setDisp] = useState(value);
  useEffect(() => {
    const m = String(value).match(/^([\d.]+)(.*)$/);
    if (!m) { setDisp(value); return; }
    const target = parseFloat(m[1]);
    const suffix = m[2];
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        io.disconnect();
        const start = performance.now();
        const dur = 1100;
        const tick = (t) => {
          const p = Math.min((t - start) / dur, 1);
          const eased = 1 - Math.pow(1 - p, 3);
          const cur = target * eased;
          setDisp((Number.isInteger(target) ? Math.round(cur) : cur.toFixed(1)) + suffix);
          if (p < 1) requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
      }
    }, { threshold: 0.6 });
    io.observe(el);
    return () => io.disconnect();
  }, [value]);
  return <span ref={ref}>{disp}</span>;
}
